import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Tag, Table, Space, Badge, Alert } from 'antd';
import Footer from '../components/Footer';
import axiosInstance from '../utils/axios';
import {
  UserOutlined,
  CarOutlined,
  FileTextOutlined,
  TeamOutlined,
  LogoutOutlined,
  CrownOutlined,
  ThunderboltOutlined,
  FileProtectOutlined,
  WifiOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  AuditOutlined,
  BarChartOutlined,
  FileExcelOutlined,
  ClockCircleOutlined,
  ArrowRightOutlined,
  CalendarOutlined,
  BellOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { hssColors, commonStyles } from '../config/theme';
import { isAdmin, clearSession } from '../utils/auth';

const PRIORIDAD_COLOR = { BAJA: 'default', MEDIA: 'blue', ALTA: 'orange', URGENTE: 'red' };
const ESTADO_COLOR   = { ACTIVO: 'processing', COMPLETADO: 'success', CANCELADO: 'error' };

const Dashboard = () => {
  const navigate = useNavigate();
  const admin = isAdmin();
  const userName = localStorage.getItem('userName') || 'Usuario';
  const userDni  = parseInt(localStorage.getItem('userDni') || '0', 10);
  const [misTareas, setMisTareas] = useState([]);
  const [loadingTareas, setLoadingTareas] = useState(true);
  const [recordatorios, setRecordatorios] = useState([]);
  const [alertas, setAlertas] = useState([]);

  useEffect(() => {
    axiosInstance.get('/alertas')
      .then(r => setAlertas(r.data.alertas || []))
      .catch(() => {});

    axiosInstance.get('/ordenes')
      .then(r => {
        const pendientes = (r.data || []).filter(
          o => o.responsable_dni === userDni && o.estado === 'ACTIVO'
        );
        setMisTareas(pendientes);
      })
      .catch(() => {})
      .finally(() => setLoadingTareas(false));

    axiosInstance.get('/agenda')
      .then(r => {
        const hoy = dayjs();
        const items = (r.data || []).filter(e => {
          const fecha = dayjs(e.fecha);
          if (e.tipo === 'CONTRATO_VENCE') return e.dias_restantes !== undefined && e.dias_restantes <= 30;
          if (e.tipo === 'PROMESA_PAGO')   return e.estado === 'PENDIENTE' && fecha.isBefore(hoy.add(3, 'day'));
          if (e.tipo === 'ORDEN')          return e.prioridad === 'URGENTE';
          return false;
        });
        setRecordatorios(items.slice(0, 8));
      })
      .catch(() => {});
  }, [userDni]);

  const columnasTareas = [
    { title: '#', dataIndex: 'id', width: 60,
      render: v => <span style={{ fontWeight: 700, color: hssColors.primary }}>#{v}</span> },
    { title: 'Tipo', dataIndex: 'tipo', width: 130,
      render: t => <Space><FileTextOutlined style={{ color: hssColors.warning }} />{t}</Space> },
    { title: 'Prioridad', dataIndex: 'prioridad', width: 100,
      render: p => <Tag color={PRIORIDAD_COLOR[p] || 'blue'} style={{ fontWeight: 600 }}>{p || 'MEDIA'}</Tag> },
    { title: 'Vehículo / Cliente', key: 'ref', render: (_, r) => {
        if (r.vehiculo) return <span>{r.vehiculo.marca} {r.vehiculo.modelo} <span style={{ color: hssColors.gray400 }}>({r.vehiculo.chapa})</span></span>;
        if (r.cliente)  return <span>{r.cliente.nombre} {r.cliente.apellido || ''}</span>;
        if (r.contacto) return <span style={{ color: hssColors.textSecondary }}>{r.contacto}</span>;
        return <span style={{ color: hssColors.gray400 }}>—</span>;
    }},
    { title: 'Fecha programada', dataIndex: 'fecha_programada', width: 150,
      render: f => f
        ? <Space><CalendarOutlined style={{ color: hssColors.info }} />{new Date(f).toLocaleDateString()}</Space>
        : <span style={{ color: hssColors.gray400 }}>—</span> },
    { title: 'Descripción', dataIndex: 'observaciones', ellipsis: true,
      render: v => v || <span style={{ color: hssColors.gray400 }}>Sin descripción</span> },
  ];

  const cerrarSesion = () => {
    clearSession();
    navigate('/');
  };

  const allMenuItems = [
    {
      title: 'Agenda',
      path: '/agenda',
      icon: <CalendarOutlined style={{ fontSize: '64px' }} />,
      color: '#16a085',
      description: 'Órdenes · Promesas de pago · Contratos',
    },
    {
      title: 'Dashboard Operativo',
      path: '/operativo',
      icon: <ThunderboltOutlined style={{ fontSize: '64px' }} />,
      color: '#e67e22',
      description: 'Monitoreo en tiempo real',
    },
    {
      title: 'Dashboard Gerencial',
      path: '/dashboard-gerencial',
      icon: <BarChartOutlined style={{ fontSize: '64px' }} />,
      color: '#d35400',
      description: 'Estadísticas y análisis del negocio',
      adminOnly: true,
    },
    {
      title: 'Clientes',
      path: '/clientes',
      icon: <UserOutlined style={{ fontSize: '64px' }} />,
      color: '#3498db',
      description: 'Gestión de clientes',
    },
    {
      title: 'Vehículos',
      path: '/vehiculos',
      icon: <CarOutlined style={{ fontSize: '64px' }} />,
      color: '#e74c3c',
      description: 'Gestión de vehículos',
    },
    {
      title: 'Órdenes de Trabajo',
      path: '/ordenes',
      icon: <FileTextOutlined style={{ fontSize: '64px' }} />,
      color: '#f39c12',
      description: 'Gestión de órdenes',
    },
    {
      title: 'Contratos Digitales',
      path: '/contratos',
      icon: <FileProtectOutlined style={{ fontSize: '64px' }} />,
      color: '#8e44ad',
      description: 'Generación y firma de contratos',
    },
    {
      title: 'GPS',
      path: '/gps',
      icon: <WifiOutlined style={{ fontSize: '64px' }} />,
      color: '#16a085',
      description: 'Dispositivos GPS y SIM cards',
    },
    {
      title: 'HSS Vision',
      path: '/vision',
      icon: <SafetyCertificateOutlined style={{ fontSize: '64px' }} />,
      color: '#8e44ad',
      description: 'DVR · Cámaras · Routers CCTV',
    },
    {
      title: 'Usuarios Clientes',
      path: '/usuarios-clientes',
      icon: <TeamOutlined style={{ fontSize: '64px' }} />,
      color: '#27ae60',
      description: 'Accesos al sistema de rastreo satelital',
    },
    {
      title: 'Reportes',
      path: '/reportes',
      icon: <FileExcelOutlined style={{ fontSize: '64px' }} />,
      color: '#27ae60',
      description: 'Exportar listas de clientes, vehículos, GPS y más',
    },
    {
      title: 'Registro de Actividad',
      path: '/audit-log',
      icon: <AuditOutlined style={{ fontSize: '64px' }} />,
      color: '#c0392b',
      description: 'Auditoría y monitoreo de comportamiento',
      adminOnly: true,
    },
    {
      title: 'Configuración',
      path: '/settings',
      icon: <SettingOutlined style={{ fontSize: '64px' }} />,
      color: '#636e72',
      description: 'Plantilla de checklist y ajustes',
      adminOnly: true,
    },
  ];

  const menuItems = allMenuItems.filter(item => !item.adminOnly || admin);

  return (
    <div style={{
      minHeight: '100vh',
      background: hssColors.backgroundPrimary,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header / Navbar */}
      <div style={{
        background: hssColors.backgroundSecondary,
        boxShadow: commonStyles.shadowMd,
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{
          padding: '16px 32px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <img
              src="/logo-hss.png"
              alt="HSS Logo"
              style={{ height: '45px', width: 'auto' }}
            />
            <div style={{ borderLeft: `2px solid ${hssColors.gray200}`, height: '40px' }} />
            <div>
              <h1 style={{
                margin: 0,
                fontSize: '24px',
                fontWeight: '700',
                color: hssColors.primary,
                lineHeight: '1.2',
              }}>
                Inicio
              </h1>
              <p style={{
                margin: 0,
                fontSize: '13px',
                color: hssColors.textSecondary,
              }}>
                Sistema de Gestión HSS
              </p>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: hssColors.textPrimary }}>{userName}</div>
            <Tag
              icon={admin ? <CrownOutlined /> : null}
              color={admin ? 'gold' : 'blue'}
              style={{ marginTop: '2px', fontSize: '11px' }}
            >
              {admin ? 'Administrador' : 'Usuario'}
            </Tag>
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <div style={{
        flex: 1,
        padding: '40px 32px',
      }}>
        {/* Mis Tareas */}
        <Card
          style={{ marginBottom: '32px', borderRadius: '12px', border: 'none', boxShadow: commonStyles.shadowMd }}
          styles={{ body: { padding: '20px 24px' } }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Space>
                <ClockCircleOutlined style={{ color: hssColors.primary, fontSize: 18 }} />
                <span style={{ fontSize: 16, fontWeight: 700, color: hssColors.primary }}>Mis Tareas Pendientes</span>
                {misTareas.length > 0 && (
                  <Badge count={misTareas.length} style={{ backgroundColor: hssColors.primary }} />
                )}
              </Space>
              <Button type="link" icon={<ArrowRightOutlined />} onClick={() => navigate('/ordenes')}
                style={{ fontWeight: 600, padding: 0 }}>
                Ver todas
              </Button>
            </div>
          }
        >
          {loadingTareas ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: hssColors.textSecondary }}>Cargando...</div>
          ) : misTareas.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: hssColors.textSecondary }}>
              No tenés tareas activas asignadas 🎉
            </div>
          ) : (
            <Table
              columns={columnasTareas}
              dataSource={misTareas}
              rowKey="id"
              size="small"
              pagination={false}
              onRow={r => ({ onClick: () => navigate('/ordenes'), style: { cursor: 'pointer' } })}
            />
          )}
        </Card>

        {/* Recordatorios */}
        {recordatorios.length > 0 && (
          <Card
            style={{ marginBottom: '24px', borderRadius: '12px', border: 'none', boxShadow: commonStyles.shadowMd, borderLeft: '4px solid #f5222d' }}
            styles={{ body: { padding: '16px 20px' } }}
            title={
              <Space>
                <BellOutlined style={{ color: '#f5222d', fontSize: 16 }} />
                <span style={{ fontWeight: 700, color: '#f5222d' }}>Recordatorios</span>
                <Badge count={recordatorios.length} style={{ backgroundColor: '#f5222d' }} />
              </Space>
            }
            extra={
              <Button type="link" icon={<ArrowRightOutlined />} onClick={() => navigate('/agenda')} style={{ fontWeight: 600, padding: 0 }}>
                Ver agenda
              </Button>
            }
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {recordatorios.map(e => {
                const icon = e.tipo === 'PROMESA_PAGO' ? <DollarOutlined /> : e.tipo === 'CONTRATO_VENCE' ? <FileProtectOutlined /> : <ExclamationCircleOutlined />;
                const color = e.tipo === 'CONTRATO_VENCE' ? (e.dias_restantes < 0 ? 'error' : 'warning') : e.tipo === 'PROMESA_PAGO' ? 'warning' : 'error';
                const irA = () => {
                  if (e.tipo === 'PROMESA_PAGO') navigate(`/clientes/${e.link_id}`);
                  else if (e.tipo === 'CONTRATO_VENCE') navigate('/contratos');
                  else navigate('/ordenes');
                };
                return (
                  <Alert
                    key={e.id}
                    type={color}
                    showIcon
                    icon={icon}
                    message={<span style={{ fontWeight: 600 }}>{e.titulo}</span>}
                    description={
                      <Space size={4}>
                        {e.ref && <span>{e.ref}</span>}
                        {e.tipo === 'CONTRATO_VENCE' && e.dias_restantes !== undefined && (
                          <Tag color={e.dias_restantes < 0 ? 'red' : 'orange'} style={{ margin: 0 }}>
                            {e.dias_restantes < 0 ? `Vencido hace ${Math.abs(e.dias_restantes)}d` : `Vence en ${e.dias_restantes}d`}
                          </Tag>
                        )}
                      </Space>
                    }
                    action={<Button size="small" type="link" onClick={irA}>Ver</Button>}
                    style={{ cursor: 'pointer', borderRadius: 8 }}
                  />
                );
              })}
            </div>
          </Card>
        )}

        {/* Alertas del sistema */}
        {alertas.length > 0 && (
          <Card
            style={{ marginBottom: '24px', borderRadius: '12px', border: 'none', boxShadow: commonStyles.shadowMd, borderLeft: '4px solid #fa8c16' }}
            styles={{ body: { padding: '16px 20px' } }}
            title={
              <Space>
                <ExclamationCircleOutlined style={{ color: '#fa8c16', fontSize: 16 }} />
                <span style={{ fontWeight: 700, color: '#fa8c16' }}>Alertas del sistema</span>
                <Badge count={alertas.length} style={{ backgroundColor: '#fa8c16' }} />
              </Space>
            }
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {alertas.map((a, i) => (
                <Alert
                  key={i}
                  type={a.nivel}
                  showIcon
                  message={<span style={{ fontWeight: 600 }}>{a.titulo}</span>}
                  description={a.descripcion}
                  action={
                    <Button size="small" type="link" onClick={() => navigate(a.link)}>
                      Ver
                    </Button>
                  }
                  style={{ cursor: 'pointer', borderRadius: 8 }}
                />
              ))}
            </div>
          </Card>
        )}

        {/* Título de sección */}
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: hssColors.textPrimary, margin: '0 0 4px 0' }}>
            Módulos del Sistema
          </h2>
          <p style={{ fontSize: '14px', color: hssColors.textSecondary, margin: 0 }}>
            Seleccione un módulo para comenzar a trabajar
          </p>
        </div>

        {/* Grid de módulos */}
        <Row gutter={[24, 24]}>
          {menuItems.map((item) => (
            <Col xs={24} sm={12} lg={8} key={item.path}>
              <Card
                hoverable
                onClick={() => navigate(item.path)}
                style={{
                  height: '100%',
                  borderRadius: '12px',
                  border: 'none',
                  boxShadow: commonStyles.shadowMd,
                  transition: commonStyles.transition,
                  cursor: 'pointer',
                  overflow: 'hidden',
                }}
                styles={{
                  body: {
                    padding: '48px 32px',
                    textAlign: 'center',
                    position: 'relative',
                  }
                }}
              >
                {/* Fondo decorativo */}
                <div style={{
                  position: 'absolute',
                  top: '-50%',
                  right: '-20%',
                  width: '150px',
                  height: '150px',
                  borderRadius: '50%',
                  background: `${item.color}15`,
                  filter: 'blur(40px)',
                  pointerEvents: 'none',
                }} />

                {/* Contenido */}
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{
                    color: item.color,
                    marginBottom: '16px',
                    display: 'flex',
                    justifyContent: 'center',
                  }}>
                    {item.icon}
                  </div>
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: hssColors.textPrimary,
                    margin: '0 0 8px 0',
                  }}>
                    {item.title}
                  </h3>
                  <p style={{
                    fontSize: '14px',
                    color: hssColors.textSecondary,
                    margin: 0,
                  }}>
                    {item.description}
                  </p>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

      </div>

      <Footer />
    </div>
  );
};

export default Dashboard;